/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import IReelManager from '../Reel/IReelManager';
import Reel from '../Reel/Reel';
import ReelManager from '../Reel/ReelManager';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import NodeProperties from '../Utility/Component/Node/NodeProperties';
import ComponentBase from '../Utility/ComponentBase';
import CLog, { LogTypes } from '../Utility/ComponentLogger';

/** @hidden */
// tslint:disable-next-line:typedef
const ccclass = cc._decorator.ccclass;
/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Supporting class for [[PayBox]]. Configures where on the paybox a payline
 * should connect based on the offset distance of the connecting paybox.
 */
@ccclass("PayBoxConnection")
export class PayBoxConnection
{

    /** The distance from the connecting paybox, in rows. Negatives are higher, positives are lower. 0 is equal. */
    @property ({
        tooltip: "The distance from the connecting paybox, in rows. " +
        "Negatives are higher, positives are lower. 0 is equal.",
        type: cc.Integer
    })
    public Distance: number = 0;

    /** Where on the paybox should this connect, where (0.0, 0.0) is the bottom left and (1.0, 1.0) is the top right. */
    @property ({
        tooltip: `Where on the paybox should this connect, where (0.0, 0.0)
                    is the bottom left and (1.0, 1.0) is the top right.`
    })
    public Point: cc.Vec2 = cc.Vec2.ZERO;
}

/**
 * Represents a paybox displayed as part of a [[VideoPaylineManager]] payline.
 */
@ccclass
export default class PayBox extends ComponentBase
{
    /** @hidden */
    @property({ type: [PayBoxConnection], visible: true })
    private _leftConnections: PayBoxConnection[] = [];
    /** [[PayBoxConnection]]s to use when connecting to a paybox on the left. */
    public get leftConnections(): PayBoxConnection[] { return this._leftConnections; }

    /** @hidden */
    @property({ type: [PayBoxConnection], visible: true })
    private _rightConnections: PayBoxConnection[] = [];
    /** [[PayBoxConnection]]s to use when connecting to a paybox on the right. */
    public get rightConnections(): PayBoxConnection[] { return this._rightConnections; }

    /** @hidden */
    @property({ type: InjectableComponent, visible: true })
    private _reelManagerRef: InjectableComponent<IReelManager> = new InjectableComponent<IReelManager>();
    /** Sets the [[IReelManager]] this paybox resides on. */
    public set ReelManager(value: IReelManager) { this._reelManagerRef.Value = value; }

    /** @hidden */
    private _animations: (cc.Animation | sp.Skeleton)[] = [];

    private _graphicsNodeRef: InjectableComponent<NodeProperties>
        = new InjectableComponent<NodeProperties>(ReferenceGroup.MainGame, 'Paybox.LineRenderer');

    /** Graphics object used to draw connecting lines. */
    private _graphics: cc.Graphics = null;

    /** @hidden */
    private _offset: number = 0;
    /** This paybox's current row offset on its reel. */
    public get Offset(): number { return this._offset; }

    @property(cc.Node)
    protected spriteNode: cc.Node = null;
    /** @hidden */
    private _sprite: cc.Sprite = null;

    private _showBox: boolean = false;

    /** @hidden */
    protected setContexts(): void
    {
        this._reelManagerRef.SetContext(ReelManager);
        this._graphicsNodeRef.SetContext(NodeProperties);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this._sprite = this.spriteNode
                ? this.spriteNode.getComponent(cc.Sprite)
                : this.getComponentInChildren(cc.Sprite);

        this._animations = [this.getComponent(cc.Animation) || this.getComponentInChildren(sp.Skeleton)];
        this.setVisible(false);
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        if (this._graphicsNodeRef.Value)
        {
            this._graphics = this._graphicsNodeRef.Value.getComponent(cc.Graphics);
        }
    }

    /**
     * Grabs the appropriate connection point from the supplied list based on the offset distance between
     * this paybox and its destination paybox.
     * @param connections The list of [[PayBoxConnection]]s to search.
     * @param distance The offset distance between the two payboxes.
     * @returns The appropriate connection point.
     */
    public ConnectionWorldPointForDistance = (connections: PayBoxConnection[], distance: number): cc.Vec2 =>
    {
        // This box is not part of the payline
        if (!this._showBox)
        {
            return this.node.convertToWorldSpaceAR(cc.Vec2.ZERO);
        }

        // Find the appropriate connection point based on how far the other symbol is.
        for (let connection of connections)
        {
            if (connection.Distance === distance)
            {
                let point: cc.Vec2
                    = new cc.Vec2(connection.Point.x * this.node.width, connection.Point.y * this.node.height);
                point.x -= this.node.anchorX * this.node.width;
                point.y -= this.node.anchorY * this.node.height;
                return this.node.convertToWorldSpaceAR(point);
            }
        }

        // If there is no connection point for the specified distanc, the paybox has been misconfigured.
        CLog.warn(`No connection found for payboxes at distance ${distance}. Check its configuration.`,
            LogTypes.Initialization, this);
        return this.node.convertToWorldSpaceAR(cc.Vec2.ZERO);
    }

    /**
     * Displays the paybox based on the supplied payline info.
     * @param showBox Whether the box outline should be shown or just the connecting line.
     * @param reelIndex The reel index this paybox is representing.
     * @param offset The paybox's row offset on the reel.
     * @param prevPaybox The previous paybox in the payline.
     * @param color What color should the box/connecting line be rendered in.
     */
    public Display( showBox: boolean,
                    reelIndex: number,
                    offset: number,
                    prevPaybox: PayBox,
                    color: cc.Color = null): void
    {
        if (this._reelManagerRef == null)
        {
            cc.warn("ReelManager not set for this instance of PayBox.");
            return;
        }

        let reel: Reel = this._reelManagerRef.Value.GetReel(reelIndex);
        if (reel == null)
        {
            CLog.warn(`Could not get reel ${reelIndex} from reel manager`, LogTypes.Initialization, this);
            return;
        }
        this._showBox = showBox;

        let position: number = reel.RootOffset - (offset * reel.SymbolHeight);

        let worlPos: cc.Vec2 = reel.node.parent.convertToWorldSpaceAR(reel.node.position);
        let localPos: cc.Vec2 = this.node.parent.convertToNodeSpaceAR(worlPos);

        this.node.x = localPos.x;
        this.node.y = position;

        if (color)
        {
            this.node.color = color;
            if (this._sprite) { this._sprite.node.color = color; }
            if (this._animations[0]) { this._animations.forEach(animation => animation.node.color = color); }
        }

        this._offset = offset;
        this.setVisible(showBox);

        if (prevPaybox)
        {
            this.drawConnection(prevPaybox, color);
        }
    }

    /** Hides the paybox and clears any connecting lines. */
    public Hide(): void
    {
        this.setVisible(false);
        this._graphics.clear();
    }

    /**
     * Draws a connecting line between this paybox and the one to its left.
     * @param prevPaybox The paybox to its left.
     * @param color What color should the connecting line be.
     */
    private drawConnection(prevPaybox: PayBox, color: cc.Color = null): void
    {
        if (this._graphics == null)
        {
            cc.warn("No Graphics renderer present for PayBox.");
            return;
        }

        if (color) { this._graphics.strokeColor = color; }

        let graphicsNode: cc.Node = this._graphics.node;
        let distance: number = this._offset - prevPaybox.Offset;

        let startPos: cc.Vec2 = this._showBox
            ? graphicsNode.convertToNodeSpaceAR(this.ConnectionWorldPointForDistance(this.leftConnections, distance))
            : graphicsNode.convertToNodeSpaceAR(this.node.convertToWorldSpaceAR(cc.Vec2.ZERO));
        let endWorldPos: cc.Vec2 = prevPaybox.ConnectionWorldPointForDistance(prevPaybox.rightConnections, distance);
        let endPos: cc.Vec2 = graphicsNode.convertToNodeSpaceAR(endWorldPos);

        this._graphics.moveTo(startPos.x, startPos.y);
        this._graphics.lineTo(endPos.x, endPos.y);
        this._graphics.stroke();
    }

    /**
     * Sets the visibility of the paybox.
     * @param to Visible or not.
     */
    private setVisible(to: boolean): void
    {
        this._sprite.enabled = to;
        this.node.children.forEach(child => child.active = to);

        if (this._animations[0] === null) { return; }
        this._animations.forEach((animation) =>
        {
            animation.enabled = to;
            if ((animation as cc.Animation).play)
            {
                (animation as cc.Animation).play();
            }
            else
            {
                (animation as sp.Skeleton).setAnimation(0, 'Pay_Anim', false);
            }
        });
    }
}
